import React, { useState, useEffect, useCallback } from 'react';
import './SongLibrary.css';

function SongLibrary({ socket, roomId, onSongSelected, serverUrl }) {
  const [songs, setSongs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);

  const fetchSongs = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${serverUrl}/api/songs`);
      if (response.ok) {
        const data = await response.json();
        setSongs(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des chansons:', error);
    } finally {
      setIsLoading(false);
    }
  }, [serverUrl]);

  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);

  const handleSongSelect = (song) => {
    setSelectedSong(song);
    
    const songData = {
      ...song,
      url: `${serverUrl}${song.path}`
    };
    
    // Informer les autres participants
    if (socket) {
      socket.emit('select-song', { roomId, songData });
    }
    
    // Callback pour le composant parent
    if (onSongSelected) {
      onSongSelected(songData);
    }
  };

  const filteredSongs = songs.filter(song => {
    const query = searchQuery.toLowerCase();
    const name = (song.name || song.originalName || '').toLowerCase();
    const artist = (song.artist || '').toLowerCase();
    const title = (song.title || '').toLowerCase();
    
    return name.includes(query) || 
           artist.includes(query) || 
           title.includes(query);
  });

  return (
    <div className="song-library">
      <div className="search-header">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="🔍 Rechercher par titre, artiste ou album..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="clear-search"
              onClick={() => setSearchQuery('')}
            >
              ✕
            </button>
          )}
        </div>
        <button 
          className="refresh-btn"
          onClick={fetchSongs}
          disabled={isLoading}
          title="Actualiser la liste des chansons"
        >
          {isLoading ? '⏳ Chargement...' : '🔄 Actualiser'}
        </button>
      </div>

      <div className="songs-count">
        {filteredSongs.length} chanson{filteredSongs.length > 1 ? 's' : ''} trouvée{filteredSongs.length > 1 ? 's' : ''}
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement des chansons...</p>
        </div>
      ) : filteredSongs.length === 0 ? (
        <div className="empty-state">
          {songs.length === 0 ? (
            <>
              <span className="empty-icon">🎵</span>
              <p>Aucune chanson disponible</p>
              <p className="empty-hint">Uploadez votre première chanson pour commencer !</p>
            </>
          ) : (
            <>
              <span className="empty-icon">🔍</span>
              <p>Aucun résultat pour "{searchQuery}"</p>
              <p className="empty-hint">Essayez un autre terme de recherche</p>
            </>
          )}
        </div>
      ) : (
        <div className="songs-list">
          {filteredSongs.map((song, index) => (
            <div 
              key={song.id || index}
              className={`song-item ${selectedSong?.id === song.id ? 'selected' : ''}`}
              onClick={() => handleSongSelect(song)}
            >
              <div className="song-icon">🎵</div>
              <div className="song-info">
                <h4 className="song-title">
                  {song.title || song.originalName || song.name || 'Sans titre'}
                </h4>
                {song.artist && (
                  <p className="song-artist">{song.artist}</p>
                )}
                {song.duration && (
                  <p className="song-duration">{formatDuration(song.duration)}</p>
                )}
              </div>
              <button 
                className="select-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSongSelect(song);
                }}
              >
                {selectedSong?.id === song.id ? '✓ Sélectionnée' : 'Sélectionner'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default SongLibrary;
